import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowCounterClockwise,
  MapTrifold,
  SkipForward,
  Target,
} from "@phosphor-icons/react";
import {
  KarelIdeComponent,
  type KarelIdeHandle,
} from "../../ide/KarelIdeComponent";
import { Button } from "../../app/components/ui/Button";
import { ExecutionControls } from "./ExecutionControls";
import { GoalThumbnail } from "./GoalThumbnail";
import { LanguageSwitch } from "./LanguageSwitch";
import { useIdeStarterCode } from "./useIdeStarterCode";
import { useLessonFinish } from "./useLessonFinish";
import { type IdeConfig, type UnitTestSpec } from "../engine/lessonTypes";
import styles from "./LessonView.module.css";

interface IdeLessonViewProps extends IdeConfig {
  onFinished: () => void;
}

function specGoal(spec: UnitTestSpec): string | undefined {
  return typeof spec === "string" ? spec : spec.goal;
}

function specTestOptions(spec: UnitTestSpec): {
  inputs?: string[];
  expectedOutput?: string;
} {
  if (typeof spec === "string") return {};
  return { inputs: spec.inputs, expectedOutput: spec.output };
}

export function IdeLessonView({
  world,
  starterCode,
  unitTests,
  io = false,
  onFinished,
}: IdeLessonViewProps) {
  const ideRef = useRef<KarelIdeHandle>(null);
  const [ready, setReady] = useState(false);
  const [running, setRunning] = useState(false);
  const [showGoals, setShowGoals] = useState(true);
  const [inputsText, setInputsText] = useState("");
  const [outputLines, setOutputLines] = useState<string[]>([]);

  const hasTests = unitTests != null;
  const [results, setResults] = useState<(boolean | null)[]>([]);
  const { scheduleFinish } = useLessonFinish(onFinished, hasTests);

  const { reset, skip } = useIdeStarterCode(starterCode, ideRef, {
    onWillLoad: () => setReady(false),
    onLoaded: () => {
      ideRef.current?.getEditor()?.focus();
      setReady(true);
    },
  });

  useEffect(() => {
    if (unitTests) {
      setShowGoals(true);
      setResults(Object.keys(unitTests).map(() => null));
      if (io) {
        const first = Object.values(unitTests)[0];
        const inputs = typeof first === "string" ? undefined : first.inputs;
        setInputsText((current) => {
          if (current !== "") return current;
          return inputs && inputs.length > 0 ? inputs.join("\n") : current;
        });
      }
    } else {
      setShowGoals(false);
      setResults([]);
    }
  }, [unitTests, io]);

  useEffect(() => {
    if (ready) setShowGoals(false);
  }, [ready]);

  const resetResults = (): void => {
    if (!unitTests) return;
    setResults(Object.keys(unitTests).map(() => null));
  };

  const handleReset = (): void => {
    resetResults();
    setReady(false);
    setRunning(false);
    void ideRef.current?.getIde()?.stopButton();
    reset();
  };

  const runUnitTests = (): void => {
    setRunning(false);
    const ide = ideRef.current?.getIde();
    if (!ide || !unitTests) return;
    const initialList = Object.keys(unitTests);
    setResults(initialList.map(() => null));

    const runTest = (index: number, failed: boolean): void => {
      if (index === initialList.length) {
        if (!failed) scheduleFinish();
        return;
      }
      const startWorld = initialList[index];
      const spec = unitTests[startWorld];
      const goal = specGoal(spec) ?? null;
      ide.runUnitTest(
        startWorld,
        goal,
        (passed) => {
          setResults((prev) => {
            const next = [...prev];
            next[index] = passed;
            return next;
          });
          setTimeout(() => runTest(index + 1, failed || !passed), 400);
        },
        specTestOptions(spec),
      );
    };
    runTest(0, false);
  };

  const readInputs = (): string[] => {
    const raw = inputsText.split("\n");
    if (raw.length > 0 && raw[raw.length - 1] === "") raw.pop();
    return raw;
  };

  const finishRun = useCallback((): void => {
    setRunning(false);
    scheduleFinish();
  }, [scheduleFinish]);

  const handleRun = (): void => {
    const ide = ideRef.current?.getIde();
    if (!ide) return;
    setRunning(true);
    setOutputLines([]);
    ide.setInputs(readInputs());
    ide.playButton(hasTests ? runUnitTests : finishRun);
  };

  const handlePause = (): void => {
    ideRef.current?.pause();
    setRunning(false);
  };

  const handleStep = (): void => {
    setRunning(false);
    ideRef.current?.step(hasTests ? runUnitTests : finishRun);
  };

  const goalMap = unitTests ?? {};
  const goals = hasTests ? Object.keys(goalMap) : [];

  return (
    <div className={styles.lesson}>
      <div className={styles.ideArea}>
        <KarelIdeComponent
          ref={ideRef}
          world={world}
          readOnly={!hasTests}
          onOutput={setOutputLines}
          panelHeader={
            <>
              <LanguageSwitch />
              <div className={styles.panelActions}>
                {ready ? (
                  <Button
                    variant="secondary"
                    icon={<ArrowCounterClockwise size={16} />}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    icon={<SkipForward size={16} />}
                    onClick={skip}
                  >
                    Skip
                  </Button>
                )}
                {hasTests && (
                  <Button
                    variant="secondary"
                    icon={
                      showGoals ? (
                        <MapTrifold size={16} />
                      ) : (
                        <Target size={16} />
                      )
                    }
                    onClick={() => setShowGoals((value) => !value)}
                  >
                    {showGoals ? "View World" : "View Goal"}
                  </Button>
                )}
                <ExecutionControls
                  ready={ready}
                  running={running}
                  onRun={handleRun}
                  onPause={handlePause}
                  onStep={handleStep}
                />
              </div>
            </>
          }
          panelOverlay={
            hasTests && showGoals ? (
              <div className={styles.goalPanel}>
                <span className={styles.goalLabel}>Goal:</span>
                <div className={styles.goals}>
                  {goals.map((start, index) => {
                    const result = results[index];
                    const goal = specGoal(goalMap[start]);
                    return (
                      <div className={styles.goalItem} key={start}>
                        {goal && <GoalThumbnail goal={goal} />}
                        {result === true ? (
                          <span
                            className={[styles.result, styles.correct].join(
                              " ",
                            )}
                          >
                            Correct!
                          </span>
                        ) : result === false ? (
                          <span
                            className={[styles.result, styles.incorrect].join(
                              " ",
                            )}
                          >
                            Try again
                          </span>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : undefined
          }
        />
        {io && (
          <div className={styles.ioPanel}>
            <div className={styles.ioColumn}>
              <label className={styles.ioLabel} htmlFor="karel-input-panel">
                Input (one value per line)
              </label>
              <textarea
                id="karel-input-panel"
                className={styles.ioInput}
                value={inputsText}
                onChange={(event) => setInputsText(event.target.value)}
                spellCheck={false}
                rows={3}
              />
            </div>
            <div className={styles.ioColumn}>
              <span className={styles.ioLabel}>Output</span>
              <pre className={styles.ioOutput}>
                <code>{outputLines.join("\n")}</code>
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
